// src/coordinated-playbook/Split/Split-SEN-Comp.js

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../Security/firebase-config";
import "../../individual-playbook/General-Page.css";
import "../Coordinated-Playbook.css";
import "../../utils/ImageHoverEffect.css";
import { applyImageZoomEffect } from "../../utils/ImageHoverEffect";
import Footer from "../../components/core-components/Footer";
import GuideImage from "../../components/core-components/GuideImage";
import Highlight from "../../components/core-components/Highlight";
import Favorites from "../../components/core-components/Favorites";
import { useBlur } from "../../context/BlurContext";
import LazyVideo from "../../components/core-components/LazyVideo";
import GuideMediaPlaceholder from "../../components/core-components/GuideMediaPlaceholder";
import MovementGuideModal from "../../components/core-components/MovementGuideModal";
import useAccordionDoubleClick from "../../hooks/useAccordionDoubleClick";
import {
  getGuideMediaPreloadSrc,
  isPlannedGuideMedia,
  plannedGuideImage,
} from "../../utils/guideMediaPlanning";

/**
 * ==========================================
 * MEDIA SOURCES (stubbed – fill in later)
 * ==========================================
 * Keys must match the leaf accordion IDs used below.
 * You can replace src paths + add more media as needed.
 */

const mediaSources = {
  /* =========================
   * DEFENSE
   * ========================= */

  "Split-DEF-Default-Setup": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Default-Setup.png")
  },

  "Split-DEF-Setup-2": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Setup-2.png")
  },

  "Split-DEF-Setup-3": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Setup-3.png")
  },

  "Split-DEF-Fighting-Setups": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Fighting-Setups.png")
  },

  "Split-DEF-Trap-Play-Setups": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Trap-Play-Setups.png")
  },

  "Split-DEF-Ult-Raze-Mid": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Ult-Raze-Mid.png")
  },

  "Split-DEF-Ult-Viper-Mid": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Ult-Viper-Mid.png")
  },

  "Split-DEF-Ult-Raze-A-Main": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Ult-Raze-A-Main.png")
  },

  "Split-DEF-Ult-Raze-B-Main": {
    overview: plannedGuideImage("coordinated/Split/SEN/DEF-Ult-Raze-B-Main.png")
  },

  /* =========================
   * ATTACK
   * ========================= */

  // Defaults
  "Split-ATK-Mid-Default": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Mid-Default.png")
  },

  "Split-ATK-Planned-Mid-to-A-Default": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Planned-Mid-to-A-Default.png")
  },

  "Split-ATK-Planned-Mid-to-B-Default": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Planned-Mid-to-B-Default.png")
  },

  "Split-ATK-A-Default": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-A-Default.png")
  },

  // Site Executes
  "Split-ATK-A-Rush-Straight": {
    overview: {
        label: "Overview",
        type: "video",
        src: "/assets/Cor/Cor-Split/CP-Split_Playbook_R_S_O_V_C-ATK-A-Hit.mp4",
        props: { ui: "minimal", loop: false }
    },
  },

  "Split-ATK-A-Ramps-Split": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-A-Ramps-Split.png")
  },

  "Split-ATK-B-Rush": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-B-Rush.png")
  },

  "Split-ATK-B-Split": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-B-Split.png")
  },

  // Fakes
  "Split-ATK-A-Ramps-Fake": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-A-Ramps-Fake.png")
  },

  "Split-ATK-B-Fake": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-B-Fake.png")
  },

  // Eco-Starts
  "Split-ATK-A-Contact": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-A-Contact.png")
  },

  "Split-ATK-B-Contact": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-B-Contact.png")
  },

  // Plans Around Ultimates
  "Split-ATK-Raze-Ult-Mid": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Raze-Ult-Mid.png")
  },

  "Split-ATK-Raze-Ult-A-Ramps": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Raze-Ult-A-Ramps.png")
  },

  "Split-ATK-Skye-Ult": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Skye-Ult.png")
  },

  "Split-ATK-Omen-Ult": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Omen-Ult.png")
  },

  "Split-ATK-Viper-Ult": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Viper-Ult.png")
  },

  "Split-ATK-Cypher-Ult": {
    overview: plannedGuideImage("coordinated/Split/SEN/ATK-Cypher-Ult.png")
  }
};

/**
 * ==========================================
 * TEXT CONTENT (placeholder text)
 * ==========================================
 * Same pattern as TemplateChapter: sectionId → mediaKey → role → text.
 * You can replace these strings with real coaching notes.
 */

const ROLES = ["general", "agent1", "agent2", "agent3", "agent4", "agent5"];

const textContent = {
  "Split-DEF-Default-Setup": {
    overview: {
      general:
        "Split / Defense / Default Setup / General: Overall description of the team’s default defensive layout with the SEN comp.",
      agent1:
        "Anchor role: main site responsibilities, where to play, and how to survive the first wave.",
      agent2:
        "Support role: utility timing, re-swings, and how to help anchor without overfighting.",
      agent3: "Rotator: when to leave the other site and how to path safely.",
      agent4: "Flex: fill gaps in the setup and watch for mid-round adjustments.",
      agent5: "Info / Sentinel: trap placement and how to gather early info safely."
    }
  },

  // You can add more detailed texts later:
  "Split-DEF-Setup-2": {
    overview: {
      general:
        "Split / Defense / Setup 2 / General: Alternate setup to punish specific opponent tendencies.",
      agent1: "Anchor notes for Setup 2.",
      agent2: "Support notes for Setup 2.",
      agent3: "Rotator notes for Setup 2.",
      agent4: "Flex notes for Setup 2.",
      agent5: "Info/sentinel notes for Setup 2."
    }
  },

  "Split-DEF-Setup-3": {
    overview: {
      general: "Split / Defense / Setup 3 / General: Third variation of your default.",
      agent1: "Anchor notes for Setup 3.",
      agent2: "Support notes for Setup 3.",
      agent3: "Rotator notes for Setup 3.",
      agent4: "Flex notes for Setup 3.",
      agent5: "Info/sentinel notes for Setup 3."
    }
  },

  "Split-DEF-Fighting-Setups": {
    overview: {
      general:
        "Split / Defense / Fighting Setups / General: Aggressive openings designed to contest space early.",
      agent1: "How Agent 1 takes first contact in fighting setups.",
      agent2: "How Agent 2 supports fights and trades.",
      agent3: "Mid-round fight timings and re-aggression.",
      agent4: "Crossfire and off-angle concepts.",
      agent5: "Utility to enable or punish aggression."
    }
  },

  "Split-DEF-Trap-Play-Setups": {
    overview: {
      general:
        "Split / Defense / Trap Plays / General: Setups designed to punish specific utility or pathing.",
      agent1: "Anchor reactions to trap activations.",
      agent2: "Utility layering for trap plays.",
      agent3: "Flank and squeeze timings.",
      agent4: "Late fight and trade roles.",
      agent5: "Trap placement details and how to avoid giving info."
    }
  },

  "Split-DEF-Ult-Raze-Mid": {
    overview: {
      general:
        "Split / Defense / Raze Ult Mid: Plan to control mid with Raze ult and supporting utility.",
      agent1: "Anchor’s job when Raze ults mid.",
      agent2: "How to support the ult and deny space.",
      agent3: "Rotations and flanks during the ult.",
      agent4: "Follow-up fights and map control.",
      agent5: "Info tools to confirm mid stacking."
    }
  },

  "Split-DEF-Ult-Viper-Mid": {
    overview: {
      general:
        "Split / Defense / Viper Ult Mid: How the comp plays around a mid Viper pit.",
      agent1: "Anchoring sites while Viper holds mid pit.",
      agent2: "Smoking and flashing around the pit.",
      agent3: "Rotate / pinch timings off pit pressure.",
      agent4: "Late round clutch positions.",
      agent5: "Trap placement to cover gaps in the pit."
    }
  },

  "Split-DEF-Ult-Raze-A-Main": {
    overview: {
      general:
        "Split / Defense / Raze Ult A-main: Forcing early control of A-main and punishing grouped attacks.",
      agent1: "Anchor positions when Raze ults A-main.",
      agent2: "Utility timing to force them into the ult.",
      agent3: "Fast rotations through mid or CT.",
      agent4: "Crossfire setups after the ult.",
      agent5: "Utility to stop re-hits."
    }
  },

  "Split-DEF-Ult-Raze-B-Main": {
    overview: {
      general:
        "Split / Defense / Raze Ult B-main: Aggressive B-main control plan with Raze ult.",
      agent1: "Holding site while B-main is contested.",
      agent2: "Supporting the ult with flashes/smokes.",
      agent3: "Flanking and re-aggressing mid.",
      agent4: "Post-ult repositioning.",
      agent5: "Info tools to confirm early B pressure."
    }
  },

  // --- Attack text placeholders (you can customize) ---

  "Split-ATK-Mid-Default": {
    overview: {
      general:
        "Split / Attack / Mid Default: Standard mid default: where everyone starts and what space you want.",
      agent1: "Entry pathing and safe angles when defaulting mid.",
      agent2: "Second-in responsibilities and trades.",
      agent3: "Lurking or pressure on side lanes.",
      agent4: "Utility to take and hold mid control.",
      agent5: "Info tools and traps to prevent flanks."
    }
  },

  "Split-ATK-Planned-Mid-to-A-Default": {
    overview: {
      general:
        "Split / Attack / Planned Mid to A Default: Structured plan to convert mid control into an A hit.",
      agent1: "Entry route when pivoting from mid to A.",
      agent2: "Utility and trading through vents/A-ramps.",
      agent3: "Backstab or late-lurk paths.",
      agent4: "Smokes/flashes used to pinch A from multiple directions.",
      agent5: "Post-plant positions planned in advance."
    }
  },

  "Split-ATK-Planned-Mid-to-B-Default": {
    overview: {
      general:
        "Split / Attack / Planned Mid to B Default: How to turn mid pressure into a B hit.",
      agent1: "Entry path from mid into B.",
      agent2: "Trading and clearing site/backsite.",
      agent3: "Lurking vents or A-ramps to punish rotates.",
      agent4: "Utility layering to block sightlines on B.",
      agent5: "Post-plant lineup spots and safe angles."
    }
  },

  "Split-ATK-A-Default": {
    overview: {
      general:
        "Split / Attack / A-Default: Slower A-lean default focusing on A-main and A-ramps control.",
      agent1: "Entries onto A site if contact is found.",
      agent2: "Utility to re-clear A-main and ramps.",
      agent3: "Mid or B pressure during A default.",
      agent4: "Smoke/flash timings on A site.",
      agent5: "Trip/utility placement to prevent fast flanks."
    }
  },

  "Split-ATK-A-Rush-Straight": {
    overview: {
      general:
        "Split / Attack / A-Rush (straight site): Fast hit with minimal defaulting.",
      agent1: "First contact and clearing key angles.",
      agent2: "Trade pathing through A-main.",
      agent3: "Late-lurk or anti-flank role.",
      agent4: "Front-loaded utility for the rush.",
      agent5: "Post-plant/nade/lineup positions."
    }
  },

  "Split-ATK-A-Ramps-Split": {
    overview: {
      general:
        "Split / Attack / A-Ramps Split: Classic two-pronged A hit through ramps and main.",
      agent1: "Entry from ramps or main (assign clearly).",
      agent2: "Trade responsibility from the opposite lane.",
      agent3: "Mid pressure that supports the split.",
      agent4: "Smokes cutting heaven and CT.",
      agent5: "Post-plant positions to cover both entries."
    }
  },

  "Split-ATK-B-Rush": {
    overview: {
      general: "Split / Attack / B-Rush: Fast B hit to punish slow rotates.",
      agent1: "Clearing close B-main and site.",
      agent2: "Second-in trade path.",
      agent3: "Optional delayed mid pressure.",
      agent4: "Utility to block crossfires.",
      agent5: "Post-plant setups for B."
    }
  },

  "Split-ATK-B-Split": {
    overview: {
      general:
        "Split / Attack / B-Split: Hitting B through mid and B-main together.",
      agent1: "Lead the hit from one lane.",
      agent2: "Lead/trade from the other lane.",
      agent3: "Lurking or anti-flank during the split.",
      agent4: "Smokes for heaven/CT crossing.",
      agent5: "Post-plant coverage and trap placement."
    }
  },

  "Split-ATK-A-Ramps-Fake": {
    overview: {
      general:
        "Split / Attack / A-Ramps Fake: Creating the illusion of an A split then pivoting away.",
      agent1: "Selling the fake without overcommitting.",
      agent2: "Utility that sells pressure.",
      agent3: "Late-hit path on the real site.",
      agent4: "Smokes/flashes that suggest a real hit.",
      agent5: "Trips and info to protect the real hit group."
    }
  },

  "Split-ATK-B-Fake": {
    overview: {
      general:
        "Split / Attack / B-Fake: Simulated B hit into an A or mid finish.",
      agent1: "Entry patterns that look real but stay safe.",
      agent2: "Utility that baits rotates.",
      agent3: "Timing the real hit on the opposite site.",
      agent4: "Utility for the real hit.",
      agent5: "Controlling space so the fake isn’t punished."
    }
  },

  "Split-ATK-A-Contact": {
    overview: {
      general:
        "Split / Attack / Eco Start: A-contact: Low-utility, close-range fights on A.",
      agent1: "Contact pathing and clearing angles.",
      agent2: "Second contact and trades.",
      agent3: "Flank watch / late push paths.",
      agent4: "Minimal utility usage on eco.",
      agent5: "Trap placement to protect eco plays."
    }
  },

  "Split-ATK-B-Contact": {
    overview: {
      general:
        "Split / Attack / Eco Start: B-contact: Walking into B with limited utility.",
      agent1: "Leading the contact play.",
      agent2: "Trade spacing behind entry.",
      agent3: "Pressure on mid to draw rotations.",
      agent4: "Utility saved for post-plant.",
      agent5: "Holding flanks with minimal resources."
    }
  },

  "Split-ATK-Raze-Ult-Mid": {
    overview: {
      general:
        "Split / Attack / Plans Around Ult: Raze Ult Mid – using Raze ult to crack mid.",
      agent1: "How to follow up the ult.",
      agent2: "Trading after the ult opens space.",
      agent3: "Lurking other lanes while mid explodes.",
      agent4: "Utility timing around the ult.",
      agent5: "Post-ult map control priorities."
    }
  },

  "Split-ATK-Raze-Ult-A-Ramps": {
    overview: {
      general:
        "Split / Attack / Raze Ult A-Ramps: Forcing ramp control with the ult.",
      agent1: "Taking ramp space with Raze.",
      agent2: "Trades and follow-up utility.",
      agent3: "Mid/B pressure to punish over-rotates.",
      agent4: "Smoke usage around ramps/heaven.",
      agent5: "Post-plant setups on A with ramp control."
    }
  },

  "Split-ATK-Skye-Ult": {
    overview: {
      general:
        "Split / Attack / Skye Ult Plan: Structuring hits around Skye ult info.",
      agent1: "Following the ults’ trail into site.",
      agent2: "Trading behind Skye ult.",
      agent3: "Lurking opposite side to punish stacking.",
      agent4: "Utility layering off the ult.",
      agent5: "Post-plant setups considering where Skye ult cleared."
    }
  },

  "Split-ATK-Omen-Ult": {
    overview: {
      general:
        "Split / Attack / Omen Ult Plan: Using Omen ult for info, pressure, or backstabs.",
      agent1: "Reacting to Omen’s info calls.",
      agent2: "Re-hit timing after the ult.",
      agent3: "Omen’s own post-ult positioning.",
      agent4: "Smokes to capitalize on chaos.",
      agent5: "Trap placements to protect slowed defaults."
    }
  },

  "Split-ATK-Viper-Ult": {
    overview: {
      general:
        "Split / Attack / Viper Ult Plan: Designing rounds around a Viper pit on site.",
      agent1: "Playing inside or around the pit.",
      agent2: "Flashes and utility through the pit.",
      agent3: "Lurks outside the pit to catch rotates.",
      agent4: "Smokes that combine with the pit.",
      agent5: "Post-plant lineups outside the pit."
    }
  },

  "Split-ATK-Cypher-Ult": {
    overview: {
      general:
        "Split / Attack / Cypher Ult Plan: How to react to Cypher ult info in mid-round.",
      agent1: "Entries based on known positions.",
      agent2: "Trades and pathing after info is revealed.",
      agent3: "Punishing over-rotates with lurks.",
      agent4: "Utility to isolate known positions.",
      agent5: "Trap placement after info is gained."
    }
  }
};

const SplitSENComp = () => {
  // Team + UI state
  const [userTeamId, setUserTeamId] = useState(null);
  const { isBlurred } = useBlur();
  const [selectedImage, setSelectedImage] = useState("image1");
  const [selectedMediaBySection, setSelectedMediaBySection] = useState({});
  const [selectedRoleBySection, setSelectedRoleBySection] = useState({});
  const [openAccordions, setOpenAccordions] = useState({});
  const [isMovementGuideOpen, setIsMovementGuideOpen] = useState(false);

  useAccordionDoubleClick({ openAccordions, setOpenAccordions });
  const location = useLocation();

  /**
   * ==========================================
   * Auto-expand accordions for highlights
   * ==========================================
   * highlightId must match the data-container-id on the content blocks.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");

    if (!highlightId) return;

    const accordionMap = {
      /* ========== DEFENSE ========== */

      "Split-SEN-DEF-Default-Setup": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Default-Setup"
      ],
      "Split-SEN-DEF-Setup-2": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Setup-2"
      ],
      "Split-SEN-DEF-Setup-3": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Setup-3"
      ],
      "Split-SEN-DEF-Fighting-Setups": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Fighting-Setups"
      ],
      "Split-SEN-DEF-Trap-Play-Setups": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Trap-Play-Setups"
      ],

      "Split-SEN-DEF-Ult-Raze-Mid": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Ult-Setups",
        "Split-DEF-Ult-Raze-Mid"
      ],
      "Split-SEN-DEF-Ult-Viper-Mid": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Ult-Setups",
        "Split-DEF-Ult-Viper-Mid"
      ],
      "Split-SEN-DEF-Ult-Raze-A-Main": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Ult-Setups",
        "Split-DEF-Ult-Raze-A-Main"
      ],
      "Split-SEN-DEF-Ult-Raze-B-Main": [
        "Split-SEN-Defense-Main",
        "Split-DEF-Ult-Setups",
        "Split-DEF-Ult-Raze-B-Main"
      ],

      /* ========== ATTACK ========== */

      // Defaults
      "Split-SEN-ATK-Mid-Default": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Defaults",
        "Split-ATK-Mid-Default"
      ],
      "Split-SEN-ATK-Planned-Mid-to-A-Default": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Defaults",
        "Split-ATK-Mid-Default",
        "Split-ATK-Planned-Mid-to-A-Default"
      ],
      "Split-SEN-ATK-Planned-Mid-to-B-Default": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Defaults",
        "Split-ATK-Mid-Default",
        "Split-ATK-Planned-Mid-to-B-Default"
      ],
      "Split-SEN-ATK-A-Default": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Defaults",
        "Split-ATK-A-Default"
      ],

      // Site Executes
      "Split-SEN-ATK-A-Rush-Straight": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Site-Executes",
        "Split-ATK-A-Rush-Straight"
      ],
      "Split-SEN-ATK-A-Ramps-Split": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Site-Executes",
        "Split-ATK-A-Ramps-Split"
      ],
      "Split-SEN-ATK-B-Rush": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Site-Executes",
        "Split-ATK-B-Rush"
      ],
      "Split-SEN-ATK-B-Split": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Site-Executes",
        "Split-ATK-B-Split"
      ],

      // Fakes
      "Split-SEN-ATK-A-Ramps-Fake": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Fakes",
        "Split-ATK-A-Ramps-Fake"
      ],
      "Split-SEN-ATK-B-Fake": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Fakes",
        "Split-ATK-B-Fake"
      ],

      // Eco Starts
      "Split-SEN-ATK-A-Contact": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Eco-Starts",
        "Split-ATK-A-Contact"
      ],
      "Split-SEN-ATK-B-Contact": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Eco-Starts",
        "Split-ATK-B-Contact"
      ],

      // Plans Around Ultimates
      "Split-SEN-ATK-Raze-Ult-Mid": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Plans-Around-Ults",
        "Split-ATK-Raze-Ult-Mid"
      ],
      "Split-SEN-ATK-Raze-Ult-A-Ramps": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Plans-Around-Ults",
        "Split-ATK-Raze-Ult-A-Ramps"
      ],
      "Split-SEN-ATK-Skye-Ult": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Plans-Around-Ults",
        "Split-ATK-Skye-Ult"
      ],
      "Split-SEN-ATK-Omen-Ult": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Plans-Around-Ults",
        "Split-ATK-Omen-Ult"
      ],
      "Split-SEN-ATK-Viper-Ult": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Plans-Around-Ults",
        "Split-ATK-Viper-Ult"
      ],
      "Split-SEN-ATK-Cypher-Ult": [
        "Split-SEN-Attack-Main",
        "Split-ATK-Plans-Around-Ults",
        "Split-ATK-Cypher-Ult"
      ]
    };

    const pathsToOpen = accordionMap[highlightId];
    if (!pathsToOpen) return;

    setOpenAccordions(prev => {
      const updated = { ...prev };
      pathsToOpen.forEach(key => {
        updated[key] = true;
      });
      sessionStorage.setItem(
        "splitSENCompAccordionState",
        JSON.stringify(updated)
      );
      return updated;
    });

    setTimeout(() => {
      const el = document.querySelector(
        `[data-container-id="${highlightId}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 500);
  }, [location.search]);

  // Hydrate accordion + media + role from sessionStorage
  useEffect(() => {
    const storedAccordionState = sessionStorage.getItem(
      "splitSENCompAccordionState"
    );
    if (storedAccordionState) {
      try {
        const parsed = JSON.parse(storedAccordionState);
        setOpenAccordions(parsed);

        const newMediaState = {};
        Object.entries(parsed).forEach(([accordionId, isOpen]) => {
          if (isOpen && mediaSources[accordionId]) {
            const firstKey = Object.keys(mediaSources[accordionId])[0];
            if (firstKey) {
              newMediaState[accordionId] = firstKey;
            }
          }
        });

        if (Object.keys(newMediaState).length > 0) {
          setSelectedMediaBySection(prev => ({
            ...prev,
            ...newMediaState
          }));
        }
      } catch (e) {
        console.error("Failed to parse accordion state", e);
      }
    }

    const storedRoles = sessionStorage.getItem("splitSENCompRoleState");
    if (storedRoles) {
      try {
        setSelectedRoleBySection(JSON.parse(storedRoles));
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    const imageContainers = document.querySelectorAll(".image-hover-effect");

    if (imageContainers.length === 0) return;

    const cleanupFunctions = [];

    imageContainers.forEach((container) => {
        const cleanup = applyImageZoomEffect(container);
        if (typeof cleanup === "function") {
        cleanupFunctions.push(cleanup);
        }
    });

    return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, []);


  // Persist selectedMediaBySection
  useEffect(() => {
    sessionStorage.setItem(
      "splitSENCompMediaState",
      JSON.stringify(selectedMediaBySection)
    );
  }, [selectedMediaBySection]);

  // Persist selectedImage
  useEffect(() => {
    sessionStorage.setItem("splitSENCompSelectedImage", selectedImage);
  }, [selectedImage]);

  // Persist role state
  useEffect(() => {
    sessionStorage.setItem(
      "splitSENCompRoleState",
      JSON.stringify(selectedRoleBySection)
    );
  }, [selectedRoleBySection]);

  // Fetch teamId from Firestore
  useEffect(() => {
    const fetchUserTeamId = async uid => {
      try {
        const userDocRef = doc(db, "users", uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          if (data.teamId) {
            setUserTeamId(data.teamId);
          } else {
            setUserTeamId(null);
          }
        } else {
          console.error("User document does not exist");
        }
      } catch (error) {
        console.error("Error fetching user team ID:", error);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        fetchUserTeamId(user.uid);
      } else {
        setUserTeamId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Page transition effect
  useEffect(() => {
    document.body.classList.add("no-transition");
    const pageContainer = document.querySelector(".page__container");
    pageContainer && pageContainer.classList.add("no-transition");

    const handleLoad = () => {
      document.body.classList.remove("no-transition");
      pageContainer && pageContainer.classList.remove("no-transition");
    };

    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  // Core accordion toggle logic
  const handleAccordionToggle = (accordionId, type) => {
    setOpenAccordions(prev => {
      const isOpen = !prev[accordionId];
      const updated = { ...prev, [accordionId]: isOpen };
      sessionStorage.setItem(
        "splitSENCompAccordionState",
        JSON.stringify(updated)
      );

      // Optional: preload media when opening
      if (isOpen && mediaSources[accordionId]) {
        const entries = Object.values(mediaSources[accordionId]);
        const firstVideoSrc = getGuideMediaPreloadSrc(entries[0]);
        if (firstVideoSrc) {
          const v0 = document.createElement("video");
          v0.src = firstVideoSrc;
          v0.preload = "auto";
          v0.load();
        }
        entries.slice(1).forEach((entry) => {
          const src = getGuideMediaPreloadSrc(entry);
          if (!src) {
            return;
          }
          const doMeta = () => {
            const v = document.createElement("video");
            v.src = src;
            v.preload = "metadata";
            v.load();
          };
          if ("requestIdleCallback" in window) {
            requestIdleCallback(doMeta, { timeout: 2000 });
          } else {
            setTimeout(doMeta, 2000);
          }
        });
      }

      if (isOpen) {
        if (type === "image") {
          setSelectedImage("image1");
        }

        if (type === "media" && mediaSources[accordionId]) {
          const firstKey = Object.keys(mediaSources[accordionId])[0];
          if (firstKey) {
            setSelectedMediaBySection(prevMedia => ({
              ...prevMedia,
              [accordionId]: firstKey
            }));
          }
        }

        // Ensure a default role is selected when section opens
        setSelectedRoleBySection(prevRoles => ({
          ...prevRoles,
          [accordionId]: prevRoles[accordionId] || "general"
        }));
      }

      return updated;
    });
  };

  const renderMediaButtons = sectionId => {
    const sectionMedia = mediaSources[sectionId];
    if (!sectionMedia) return null;

    return (
      <>
        <button
          type="button"
          onClick={() => setIsMovementGuideOpen(true)}
          className="movement-guide-question-button"
        >
          ?
        </button>
        {Object.entries(sectionMedia).map(([key, { label }]) => (
          <button
            key={key}
            onClick={() =>
              setSelectedMediaBySection(prev => ({
                ...prev,
                [sectionId]: key
              }))
            }
            className={selectedMediaBySection[sectionId] === key ? "active" : ""}
          >
            {label}
          </button>
        ))}
      </>
    );
  };

  const renderSelectedMedia = sectionId => {
    const selectedKey = selectedMediaBySection[sectionId];
    const sectionMedia = mediaSources[sectionId];

    if (!selectedKey || !sectionMedia || !sectionMedia[selectedKey]) return null;

    const entry = sectionMedia[selectedKey];
    const { type, src, props = {} } = entry;

    if (isPlannedGuideMedia(entry)) {
      return <GuideMediaPlaceholder key={selectedKey} entry={entry} withFrame={false} />;
    }

    if (type === "image") {
      return <GuideImage key={selectedKey} src={src} alt={selectedKey} {...props} />;
    }

    if (type === "video") {
      return (
        <LazyVideo
          key={selectedKey}
          src={src}
          preload="auto"
          forceLoad={true}
          {...props}
        />
      );
    }

    return null;
  };



  const renderRoleButtons = sectionId => {
    const currentRole = selectedRoleBySection[sectionId] || "general";

    return (
      <div className="role-selector">
        {ROLES.map(roleKey => {
          const label =
            roleKey === "general"
              ? "General"
              : `Agent ${roleKey.replace("agent", "")}`;
          const isActive = currentRole === roleKey;
          return (
            <button
              key={roleKey}
              type="button"
              className={isActive ? "active" : ""}
              onClick={() =>
                setSelectedRoleBySection(prev => ({
                  ...prev,
                  [sectionId]: roleKey
                }))
              }
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  const getTextForSection = sectionId => {
    const mediaKey = selectedMediaBySection[sectionId];
    if (!mediaKey)
      return "Select a media option above to see detailed notes.";

    const roleKey = selectedRoleBySection[sectionId] || "general";

    const sectionText = textContent[sectionId];
    if (!sectionText) {
      return "No text content configured yet for this section. Add entries to textContent in Split-SEN-Comp.js.";
    }

    const mediaText = sectionText[mediaKey];
    if (!mediaText) {
      return "No text content configured for this media option yet.";
    }

    const specificText =
      mediaText[roleKey] || mediaText.general || "No text for this role yet.";

    return specificText;
  };

  return (
    <div className={`page__container ${isBlurred ? "blurred-content" : ""}`}>
      <MovementGuideModal
        isOpen={isMovementGuideOpen}
        onClose={() => setIsMovementGuideOpen(false)}
      />
      <Highlight />

      <main
        className={`Main__content__container ${
          isBlurred ? "blurred-content" : ""
        }`}
      >
        <h1 className="M-A-Title">Split – SEN Comp</h1>

        <ul className="accordian">
          {/* ==================================
              1. DEFENSE
              ================================== */}
          <li>
            <input
              type="checkbox"
              name="accordion-defense"
              id="Split-SEN-Defense-Main"
              checked={!!openAccordions["Split-SEN-Defense-Main"]}
              onChange={() => handleAccordionToggle("Split-SEN-Defense-Main")}
            />
            <label htmlFor="Split-SEN-Defense-Main">
              Defense – SEN Comp
            </label>

            <div className="accordian__content">
              <ul>
                {/* 1.1 Default Setup */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="Split-DEF-Default-Setup"
                    checked={!!openAccordions["Split-DEF-Default-Setup"]}
                    onChange={() =>
                      handleAccordionToggle("Split-DEF-Default-Setup", "media")
                    }
                  />
                  <label htmlFor="Split-DEF-Default-Setup">
                    Default Setup
                    <Favorites
                      headingId="Split-DEF-Default-Setup"
                      headingContent="Default Setup"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="Split-SEN-DEF-Default-Setup"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("Split-DEF-Default-Setup")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="Split-DEF-Default-Setup"
                        >
                          {renderSelectedMedia("Split-DEF-Default-Setup")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {renderRoleButtons("Split-DEF-Default-Setup")}
                        <p>{getTextForSection("Split-DEF-Default-Setup")}</p>
                      </div>
                    </div>
                  </div>
                </li>

                {/* 1.2 Setup 2 */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="Split-DEF-Setup-2"
                    checked={!!openAccordions["Split-DEF-Setup-2"]}
                    onChange={() =>
                      handleAccordionToggle("Split-DEF-Setup-2", "media")
                    }
                  />
                  <label htmlFor="Split-DEF-Setup-2">
                    Setup 2
                    <Favorites
                      headingId="Split-DEF-Setup-2"
                      headingContent="Setup 2"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="Split-SEN-DEF-Setup-2"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("Split-DEF-Setup-2")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="Split-DEF-Setup-2"
                        >
                          {renderSelectedMedia("Split-DEF-Setup-2")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {renderRoleButtons("Split-DEF-Setup-2")}
                        <p>{getTextForSection("Split-DEF-Setup-2")}</p>
                      </div>
                    </div>
                  </div>
                </li>

                {/* 1.3 Setup 3 */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="Split-DEF-Setup-3"
                    checked={!!openAccordions["Split-DEF-Setup-3"]}
                    onChange={() =>
                      handleAccordionToggle("Split-DEF-Setup-3", "media")
                    }
                  />
                  <label htmlFor="Split-DEF-Setup-3">
                    Setup 3
                    <Favorites
                      headingId="Split-DEF-Setup-3"
                      headingContent="Setup 3"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="Split-SEN-DEF-Setup-3"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("Split-DEF-Setup-3")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="Split-DEF-Setup-3"
                        >
                          {renderSelectedMedia("Split-DEF-Setup-3")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {renderRoleButtons("Split-DEF-Setup-3")}
                        <p>{getTextForSection("Split-DEF-Setup-3")}</p>
                      </div>
                    </div>
                  </div>
                </li>

                {/* 1.4 Fighting Setups */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="Split-DEF-Fighting-Setups"
                    checked={!!openAccordions["Split-DEF-Fighting-Setups"]}
                    onChange={() =>
                      handleAccordionToggle(
                        "Split-DEF-Fighting-Setups",
                        "media"
                      )
                    }
                  />
                  <label htmlFor="Split-DEF-Fighting-Setups">
                    Fighting Setups
                    <Favorites
                      headingId="Split-DEF-Fighting-Setups"
                      headingContent="Fighting Setups"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="Split-SEN-DEF-Fighting-Setups"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("Split-DEF-Fighting-Setups")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="Split-DEF-Fighting-Setups"
                        >
                          {renderSelectedMedia("Split-DEF-Fighting-Setups")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {renderRoleButtons("Split-DEF-Fighting-Setups")}
                        <p>
                          {getTextForSection("Split-DEF-Fighting-Setups")}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>

                {/* 1.5 Trap Play Setups */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="Split-DEF-Trap-Play-Setups"
                    checked={!!openAccordions["Split-DEF-Trap-Play-Setups"]}
                    onChange={() =>
                      handleAccordionToggle(
                        "Split-DEF-Trap-Play-Setups",
                        "media"
                      )
                    }
                  />
                  <label htmlFor="Split-DEF-Trap-Play-Setups">
                    Trap Play Setups
                    <Favorites
                      headingId="Split-DEF-Trap-Play-Setups"
                      headingContent="Trap Play Setups"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="Split-SEN-DEF-Trap-Play-Setups"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("Split-DEF-Trap-Play-Setups")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="Split-DEF-Trap-Play-Setups"
                        >
                          {renderSelectedMedia("Split-DEF-Trap-Play-Setups")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {renderRoleButtons("Split-DEF-Trap-Play-Setups")}
                        <p>
                          {getTextForSection("Split-DEF-Trap-Play-Setups")}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>

                {/* 1.6 Ult Setups (parent) */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="Split-DEF-Ult-Setups"
                    checked={!!openAccordions["Split-DEF-Ult-Setups"]}
                    onChange={() =>
                      handleAccordionToggle("Split-DEF-Ult-Setups")
                    }
                  />
                  <label htmlFor="Split-DEF-Ult-Setups">
                    Ult Setups
                    <Favorites
                      headingId="Split-DEF-Ult-Setups"
                      headingContent="Ult Setups"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div className="accordian__content">
                    <ul>
                      {/* 1.6.1 Raze Ult Mid */}
                      <li>
                        <input
                          type="checkbox"
                          name="Defense-Ult-sub"
                          id="Split-DEF-Ult-Raze-Mid"
                          checked={!!openAccordions["Split-DEF-Ult-Raze-Mid"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-DEF-Ult-Raze-Mid",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-DEF-Ult-Raze-Mid">
                          Raze Ult Mid
                          <Favorites
                            headingId="Split-DEF-Ult-Raze-Mid"
                            headingContent="Raze Ult Mid (Defense)"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-DEF-Ult-Raze-Mid"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-DEF-Ult-Raze-Mid")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-DEF-Ult-Raze-Mid"
                              >
                                {renderSelectedMedia("Split-DEF-Ult-Raze-Mid")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-DEF-Ult-Raze-Mid")}
                              <p>
                                {getTextForSection("Split-DEF-Ult-Raze-Mid")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 1.6.2 Viper Ult Mid */}
                      <li>
                        <input
                          type="checkbox"
                          name="Defense-Ult-sub"
                          id="Split-DEF-Ult-Viper-Mid"
                          checked={
                            !!openAccordions["Split-DEF-Ult-Viper-Mid"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-DEF-Ult-Viper-Mid",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-DEF-Ult-Viper-Mid">
                          Viper Ult Mid
                          <Favorites
                            headingId="Split-DEF-Ult-Viper-Mid"
                            headingContent="Viper Ult Mid (Defense)"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-DEF-Ult-Viper-Mid"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-DEF-Ult-Viper-Mid")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-DEF-Ult-Viper-Mid"
                              >
                                {renderSelectedMedia(
                                  "Split-DEF-Ult-Viper-Mid"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-DEF-Ult-Viper-Mid")}
                              <p>
                                {getTextForSection("Split-DEF-Ult-Viper-Mid")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 1.6.3 Raze Ult A-main */}
                      <li>
                        <input
                          type="checkbox"
                          name="Defense-Ult-sub"
                          id="Split-DEF-Ult-Raze-A-Main"
                          checked={
                            !!openAccordions["Split-DEF-Ult-Raze-A-Main"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-DEF-Ult-Raze-A-Main",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-DEF-Ult-Raze-A-Main">
                          Raze Ult A-main
                          <Favorites
                            headingId="Split-DEF-Ult-Raze-A-Main"
                            headingContent="Raze Ult A-main (Defense)"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-DEF-Ult-Raze-A-Main"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-DEF-Ult-Raze-A-Main")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-DEF-Ult-Raze-A-Main"
                              >
                                {renderSelectedMedia(
                                  "Split-DEF-Ult-Raze-A-Main"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-DEF-Ult-Raze-A-Main")}
                              <p>
                                {getTextForSection("Split-DEF-Ult-Raze-A-Main")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 1.6.4 Raze Ult B-main */}
                      <li>
                        <input
                          type="checkbox"
                          name="Defense-Ult-sub"
                          id="Split-DEF-Ult-Raze-B-Main"
                          checked={
                            !!openAccordions["Split-DEF-Ult-Raze-B-Main"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-DEF-Ult-Raze-B-Main",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-DEF-Ult-Raze-B-Main">
                          Raze Ult B-main
                          <Favorites
                            headingId="Split-DEF-Ult-Raze-B-Main"
                            headingContent="Raze Ult B-main (Defense)"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-DEF-Ult-Raze-B-Main"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-DEF-Ult-Raze-B-Main")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-DEF-Ult-Raze-B-Main"
                              >
                                {renderSelectedMedia(
                                  "Split-DEF-Ult-Raze-B-Main"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-DEF-Ult-Raze-B-Main")}
                              <p>
                                {getTextForSection("Split-DEF-Ult-Raze-B-Main")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </li>

          {/* ==================================
              2. ATTACK
              ================================== */}
          <li>
            <input
              type="checkbox"
              name="accordion-attack"
              id="Split-SEN-Attack-Main"
              checked={!!openAccordions["Split-SEN-Attack-Main"]}
              onChange={() => handleAccordionToggle("Split-SEN-Attack-Main")}
            />
            <label htmlFor="Split-SEN-Attack-Main">
              Attack – SEN Comp
            </label>

            <div className="accordian__content">
              <ul>
                {/* 2.1 Defaults */}
                <li>
                  <input
                    type="checkbox"
                    name="Attack-sub"
                    id="Split-ATK-Defaults"
                    checked={!!openAccordions["Split-ATK-Defaults"]}
                    onChange={() =>
                      handleAccordionToggle("Split-ATK-Defaults")
                    }
                  />
                  <label htmlFor="Split-ATK-Defaults">
                    Defaults
                    <Favorites
                      headingId="Split-ATK-Defaults"
                      headingContent="Defaults"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div className="accordian__content">
                    <ul>
                      {/* 2.1.1 Mid Default */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Defaults-sub"
                          id="Split-ATK-Mid-Default"
                          checked={!!openAccordions["Split-ATK-Mid-Default"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Mid-Default",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Mid-Default">
                          Mid Default
                          <Favorites
                            headingId="Split-ATK-Mid-Default"
                            headingContent="Mid Default"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Mid-Default"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Mid-Default")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Mid-Default"
                              >
                                {renderSelectedMedia("Split-ATK-Mid-Default")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Mid-Default")}
                              <p>
                                {getTextForSection("Split-ATK-Mid-Default")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.1.1.1 Planned Mid to A Default */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Defaults-sub"
                          id="Split-ATK-Planned-Mid-to-A-Default"
                          checked={
                            !!openAccordions[
                              "Split-ATK-Planned-Mid-to-A-Default"
                            ]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Planned-Mid-to-A-Default",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Planned-Mid-to-A-Default">
                          Planned Mid to A Default
                          <Favorites
                            headingId="Split-ATK-Planned-Mid-to-A-Default"
                            headingContent="Planned Mid to A Default"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Planned-Mid-to-A-Default"
                        >
                          <div className="image-selector">
                            {renderMediaButtons(
                              "Split-ATK-Planned-Mid-to-A-Default"
                            )}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Planned-Mid-to-A-Default"
                              >
                                {renderSelectedMedia(
                                  "Split-ATK-Planned-Mid-to-A-Default"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons(
                                "Split-ATK-Planned-Mid-to-A-Default"
                              )}
                              <p>
                                {getTextForSection(
                                  "Split-ATK-Planned-Mid-to-A-Default"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.1.1.2 Planned Mid to B Default */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Defaults-sub"
                          id="Split-ATK-Planned-Mid-to-B-Default"
                          checked={
                            !!openAccordions[
                              "Split-ATK-Planned-Mid-to-B-Default"
                            ]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Planned-Mid-to-B-Default",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Planned-Mid-to-B-Default">
                          Planned Mid to B Default
                          <Favorites
                            headingId="Split-ATK-Planned-Mid-to-B-Default"
                            headingContent="Planned Mid to B Default"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Planned-Mid-to-B-Default"
                        >
                          <div className="image-selector">
                            {renderMediaButtons(
                              "Split-ATK-Planned-Mid-to-B-Default"
                            )}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Planned-Mid-to-B-Default"
                              >
                                {renderSelectedMedia(
                                  "Split-ATK-Planned-Mid-to-B-Default"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons(
                                "Split-ATK-Planned-Mid-to-B-Default"
                              )}
                              <p>
                                {getTextForSection(
                                  "Split-ATK-Planned-Mid-to-B-Default"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.1.2 A-Default */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Defaults-sub"
                          id="Split-ATK-A-Default"
                          checked={!!openAccordions["Split-ATK-A-Default"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-A-Default",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-A-Default">
                          A-Default
                          <Favorites
                            headingId="Split-ATK-A-Default"
                            headingContent="A-Default"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-A-Default"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-A-Default")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-A-Default"
                              >
                                {renderSelectedMedia("Split-ATK-A-Default")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-A-Default")}
                              <p>{getTextForSection("Split-ATK-A-Default")}</p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* 2.2 Site Executes */}
                <li>
                  <input
                    type="checkbox"
                    name="Attack-sub"
                    id="Split-ATK-Site-Executes"
                    checked={!!openAccordions["Split-ATK-Site-Executes"]}
                    onChange={() =>
                      handleAccordionToggle("Split-ATK-Site-Executes")
                    }
                  />
                  <label htmlFor="Split-ATK-Site-Executes">
                    Site Executes
                    <Favorites
                      headingId="Split-ATK-Site-Executes"
                      headingContent="Site Executes"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div className="accordian__content">
                    <ul>
                      {/* 2.2.1 A-Rush (straight site) */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Executes-sub"
                          id="Split-ATK-A-Rush-Straight"
                          checked={
                            !!openAccordions["Split-ATK-A-Rush-Straight"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-A-Rush-Straight",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-A-Rush-Straight">
                          A-Rush (straight site)
                          <Favorites
                            headingId="Split-ATK-A-Rush-Straight"
                            headingContent="A-Rush (straight site)"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-A-Rush-Straight"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-A-Rush-Straight")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-A-Rush-Straight"
                              >
                                {renderSelectedMedia(
                                  "Split-ATK-A-Rush-Straight"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-A-Rush-Straight")}
                              <p>
                                {getTextForSection(
                                  "Split-ATK-A-Rush-Straight"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.2.2 A-Ramps Split */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Executes-sub"
                          id="Split-ATK-A-Ramps-Split"
                          checked={
                            !!openAccordions["Split-ATK-A-Ramps-Split"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-A-Ramps-Split",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-A-Ramps-Split">
                          A-Ramps Split
                          <Favorites
                            headingId="Split-ATK-A-Ramps-Split"
                            headingContent="A-Ramps Split"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-A-Ramps-Split"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-A-Ramps-Split")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-A-Ramps-Split"
                              >
                                {renderSelectedMedia("Split-ATK-A-Ramps-Split")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-A-Ramps-Split")}
                              <p>
                                {getTextForSection("Split-ATK-A-Ramps-Split")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.2.3 B-Rush */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Executes-sub"
                          id="Split-ATK-B-Rush"
                          checked={!!openAccordions["Split-ATK-B-Rush"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-B-Rush",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-B-Rush">
                          B-Rush
                          <Favorites
                            headingId="Split-ATK-B-Rush"
                            headingContent="B-Rush"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-B-Rush"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-B-Rush")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-B-Rush"
                              >
                                {renderSelectedMedia("Split-ATK-B-Rush")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-B-Rush")}
                              <p>{getTextForSection("Split-ATK-B-Rush")}</p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.2.4 B-Split */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Executes-sub"
                          id="Split-ATK-B-Split"
                          checked={!!openAccordions["Split-ATK-B-Split"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-B-Split",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-B-Split">
                          B-Split
                          <Favorites
                            headingId="Split-ATK-B-Split"
                            headingContent="B-Split"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-B-Split"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-B-Split")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-B-Split"
                              >
                                {renderSelectedMedia("Split-ATK-B-Split")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-B-Split")}
                              <p>{getTextForSection("Split-ATK-B-Split")}</p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* 2.3 Fakes */}
                <li>
                  <input
                    type="checkbox"
                    name="Attack-sub"
                    id="Split-ATK-Fakes"
                    checked={!!openAccordions["Split-ATK-Fakes"]}
                    onChange={() => handleAccordionToggle("Split-ATK-Fakes")}
                  />
                  <label htmlFor="Split-ATK-Fakes">
                    Fakes
                    <Favorites
                      headingId="Split-ATK-Fakes"
                      headingContent="Fakes"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div className="accordian__content">
                    <ul>
                      {/* 2.3.1 A-Ramps fake */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Fakes-sub"
                          id="Split-ATK-A-Ramps-Fake"
                          checked={
                            !!openAccordions["Split-ATK-A-Ramps-Fake"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-A-Ramps-Fake",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-A-Ramps-Fake">
                          A-Ramps fake
                          <Favorites
                            headingId="Split-ATK-A-Ramps-Fake"
                            headingContent="A-Ramps fake"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-A-Ramps-Fake"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-A-Ramps-Fake")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-A-Ramps-Fake"
                              >
                                {renderSelectedMedia("Split-ATK-A-Ramps-Fake")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-A-Ramps-Fake")}
                              <p>
                                {getTextForSection("Split-ATK-A-Ramps-Fake")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.3.2 B-Fake */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Fakes-sub"
                          id="Split-ATK-B-Fake"
                          checked={!!openAccordions["Split-ATK-B-Fake"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-B-Fake",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-B-Fake">
                          B-Fake
                          <Favorites
                            headingId="Split-ATK-B-Fake"
                            headingContent="B-Fake"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-B-Fake"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-B-Fake")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-B-Fake"
                              >
                                {renderSelectedMedia("Split-ATK-B-Fake")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-B-Fake")}
                              <p>{getTextForSection("Split-ATK-B-Fake")}</p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* 2.4 Eco-Starts */}
                <li>
                  <input
                    type="checkbox"
                    name="Attack-sub"
                    id="Split-ATK-Eco-Starts"
                    checked={!!openAccordions["Split-ATK-Eco-Starts"]}
                    onChange={() =>
                      handleAccordionToggle("Split-ATK-Eco-Starts")
                    }
                  />
                  <label htmlFor="Split-ATK-Eco-Starts">
                    Eco-Starts
                    <Favorites
                      headingId="Split-ATK-Eco-Starts"
                      headingContent="Eco-Starts"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div className="accordian__content">
                    <ul>
                      {/* 2.4.1 A-contact */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Eco-sub"
                          id="Split-ATK-A-Contact"
                          checked={!!openAccordions["Split-ATK-A-Contact"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-A-Contact",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-A-Contact">
                          A-contact
                          <Favorites
                            headingId="Split-ATK-A-Contact"
                            headingContent="A-contact"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-A-Contact"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-A-Contact")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-A-Contact"
                              >
                                {renderSelectedMedia("Split-ATK-A-Contact")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-A-Contact")}
                              <p>
                                {getTextForSection("Split-ATK-A-Contact")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.4.2 B-contact */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Eco-sub"
                          id="Split-ATK-B-Contact"
                          checked={!!openAccordions["Split-ATK-B-Contact"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-B-Contact",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-B-Contact">
                          B-contact
                          <Favorites
                            headingId="Split-ATK-B-Contact"
                            headingContent="B-contact"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-B-Contact"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-B-Contact")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-B-Contact"
                              >
                                {renderSelectedMedia("Split-ATK-B-Contact")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-B-Contact")}
                              <p>
                                {getTextForSection("Split-ATK-B-Contact")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* 2.5 Plans Around Ultimates */}
                <li>
                  <input
                    type="checkbox"
                    name="Attack-sub"
                    id="Split-ATK-Plans-Around-Ults"
                    checked={!!openAccordions["Split-ATK-Plans-Around-Ults"]}
                    onChange={() =>
                      handleAccordionToggle("Split-ATK-Plans-Around-Ults")
                    }
                  />
                  <label htmlFor="Split-ATK-Plans-Around-Ults">
                    Plans Around Ultimate’s
                    <Favorites
                      headingId="Split-ATK-Plans-Around-Ults"
                      headingContent="Plans Around Ultimate’s"
                      pageLocation="/Split-SEN-Comp"
                      teamId={userTeamId}
                    />
                  </label>

                  <div className="accordian__content">
                    <ul>
                      {/* 2.5.1 Raze Ult Mid */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Ult-sub"
                          id="Split-ATK-Raze-Ult-Mid"
                          checked={
                            !!openAccordions["Split-ATK-Raze-Ult-Mid"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Raze-Ult-Mid",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Raze-Ult-Mid">
                          Raze Ult Mid
                          <Favorites
                            headingId="Split-ATK-Raze-Ult-Mid"
                            headingContent="Raze Ult Mid (Attack)"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Raze-Ult-Mid"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Raze-Ult-Mid")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Raze-Ult-Mid"
                              >
                                {renderSelectedMedia("Split-ATK-Raze-Ult-Mid")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Raze-Ult-Mid")}
                              <p>
                                {getTextForSection(
                                  "Split-ATK-Raze-Ult-Mid"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.5.2 Raze Ult A-Ramps */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Ult-sub"
                          id="Split-ATK-Raze-Ult-A-Ramps"
                          checked={
                            !!openAccordions["Split-ATK-Raze-Ult-A-Ramps"]
                          }
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Raze-Ult-A-Ramps",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Raze-Ult-A-Ramps">
                          Raze Ult A-Ramps
                          <Favorites
                            headingId="Split-ATK-Raze-Ult-A-Ramps"
                            headingContent="Raze Ult A-Ramps"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Raze-Ult-A-Ramps"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Raze-Ult-A-Ramps")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Raze-Ult-A-Ramps"
                              >
                                {renderSelectedMedia(
                                  "Split-ATK-Raze-Ult-A-Ramps"
                                )}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Raze-Ult-A-Ramps")}
                              <p>
                                {getTextForSection(
                                  "Split-ATK-Raze-Ult-A-Ramps"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.5.3 Skye Ult */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Ult-sub"
                          id="Split-ATK-Skye-Ult"
                          checked={!!openAccordions["Split-ATK-Skye-Ult"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Skye-Ult",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Skye-Ult">
                          Skye Ult
                          <Favorites
                            headingId="Split-ATK-Skye-Ult"
                            headingContent="Skye Ult"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Skye-Ult"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Skye-Ult")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Skye-Ult"
                              >
                                {renderSelectedMedia("Split-ATK-Skye-Ult")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Skye-Ult")}
                              <p>{getTextForSection("Split-ATK-Skye-Ult")}</p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.5.4 Omen Ult */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Ult-sub"
                          id="Split-ATK-Omen-Ult"
                          checked={!!openAccordions["Split-ATK-Omen-Ult"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Omen-Ult",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Omen-Ult">
                          Omen Ult
                          <Favorites
                            headingId="Split-ATK-Omen-Ult"
                            headingContent="Omen Ult"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Omen-Ult"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Omen-Ult")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Omen-Ult"
                              >
                                {renderSelectedMedia("Split-ATK-Omen-Ult")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Omen-Ult")}
                              <p>{getTextForSection("Split-ATK-Omen-Ult")}</p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.5.5 Viper Ult */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Ult-sub"
                          id="Split-ATK-Viper-Ult"
                          checked={!!openAccordions["Split-ATK-Viper-Ult"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Viper-Ult",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Viper-Ult">
                          Viper Ult
                          <Favorites
                            headingId="Split-ATK-Viper-Ult"
                            headingContent="Viper Ult"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Viper-Ult"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Viper-Ult")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Viper-Ult"
                              >
                                {renderSelectedMedia("Split-ATK-Viper-Ult")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Viper-Ult")}
                              <p>{getTextForSection("Split-ATK-Viper-Ult")}</p>
                            </div>
                          </div>
                        </div>
                      </li>

                      {/* 2.5.6 Cypher Ult */}
                      <li>
                        <input
                          type="checkbox"
                          name="Attack-Ult-sub"
                          id="Split-ATK-Cypher-Ult"
                          checked={!!openAccordions["Split-ATK-Cypher-Ult"]}
                          onChange={() =>
                            handleAccordionToggle(
                              "Split-ATK-Cypher-Ult",
                              "media"
                            )
                          }
                        />
                        <label htmlFor="Split-ATK-Cypher-Ult">
                          Cypher Ult
                          <Favorites
                            headingId="Split-ATK-Cypher-Ult"
                            headingContent="Cypher Ult"
                            pageLocation="/Split-SEN-Comp"
                            teamId={userTeamId}
                          />
                        </label>

                        <div
                          className="accordian__content"
                          data-container-id="Split-SEN-ATK-Cypher-Ult"
                        >
                          <div className="image-selector">
                            {renderMediaButtons("Split-ATK-Cypher-Ult")}
                          </div>

                          <div className="retake-content__wrapper">
                            <div className="image__container">
                              <div
                                className="Image-Border image-hover-effect"
                                data-zoom-id="Split-ATK-Cypher-Ult"
                              >
                                {renderSelectedMedia("Split-ATK-Cypher-Ult")}
                              </div>
                            </div>

                            <div className="retake-text__container">
                              {renderRoleButtons("Split-ATK-Cypher-Ult")}
                              <p>{getTextForSection("Split-ATK-Cypher-Ult")}</p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </main>

      <section className="footer-section">
        <Footer />
      </section>
    </div>
  );
};

export default SplitSENComp;
